import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";
import { Link } from "react-router-dom";
import {
  Award, CheckCircle2, Clock, Package, Download, Hash, Calendar,
  ShieldCheck, AlertTriangle, ShoppingCart, Printer,
} from "lucide-react";

const PLAN_LABELS = { "3m": "3 mois", "6m": "6 mois", "12m": "12 mois" };

function LicenseCard({ lic, onPrint }) {
  const activated = lic.activated_at ? new Date(lic.activated_at).toISOString().slice(0, 10) : "—";
  const expires = lic.expires_at ? new Date(lic.expires_at).toISOString().slice(0, 10) : "—";
  const daysLeft = typeof lic.days_remaining === "number" ? lic.days_remaining : null;
  const expired = !!lic.is_expired;
  const expiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 14;

  return (
    <div
      data-testid={`lic3p-${lic.id}`}
      className={`card-lhs p-5 ${expired ? "border-zinc-700 opacity-70" : "border-orange-500/40 orange-glow"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
            <Package className="w-6 h-6 text-orange-500" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-base truncate">{lic.tool_name}</div>
            <div className="text-[11px] text-zinc-500 font-mono-tech">{lic.activation_ref || lic.order_number}</div>
          </div>
        </div>
        <span className={`shrink-0 text-[10px] px-2.5 py-1 rounded-full border font-mono-tech ${
          expired
            ? "text-zinc-400 border-zinc-500/40 bg-zinc-500/10"
            : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
        }`}>
          {expired ? "EXPIRÉE" : "ACTIVÉE"}
        </span>
      </div>

      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between py-1 border-b border-zinc-800/60">
          <span className="text-zinc-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Durée</span>
          <span className="font-semibold">{PLAN_LABELS[lic.plan] || lic.plan.toUpperCase()}</span>
        </div>
        <div className="flex justify-between py-1 border-b border-zinc-800/60">
          <span className="text-zinc-500 flex items-center gap-1.5">💰 Montant</span>
          <span className="font-semibold font-mono-tech">{lic.price} {lic.currency}</span>
        </div>
        <div className="flex justify-between py-1 border-b border-zinc-800/60">
          <span className="text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Activée</span>
          <span className="font-mono-tech text-xs">{activated}</span>
        </div>
        <div className="flex justify-between py-1 border-b border-zinc-800/60">
          <span className="text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Expire</span>
          <span className="font-mono-tech text-xs">{expires}</span>
        </div>
        <div className="flex justify-between py-1 border-b border-zinc-800/60">
          <span className="text-zinc-500 flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" /> Référence</span>
          <span className="font-mono-tech text-xs text-orange-400">{lic.activation_ref || "—"}</span>
        </div>
        <div className="flex justify-between py-1">
          <span className="text-zinc-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Statut</span>
          <span className={`font-bold ${expired ? "text-zinc-400" : "text-emerald-400"}`}>
            {expired ? "EXPIRÉE" : "ACTIVÉE"}
          </span>
        </div>
      </div>

      {!expired && expiringSoon && (
        <div className="mt-3 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Expire dans <b>{daysLeft}</b> jour{daysLeft > 1 ? "s" : ""}
        </div>
      )}
      {!expired && daysLeft !== null && daysLeft > 14 && (
        <div className="mt-3 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" /> {daysLeft} jours restants
        </div>
      )}

      <button
        data-testid={`lic3p-${lic.id}-print`}
        onClick={() => onPrint(lic)}
        className="w-full btn-ghost text-xs py-2 rounded-lg mt-3 inline-flex items-center justify-center gap-2"
      >
        <Printer className="w-3.5 h-3.5" /> Imprimer la preuve
      </button>
    </div>
  );
}

function PrintableProof({ lic, user, onClose }) {
  const activated = lic.activated_at ? new Date(lic.activated_at).toISOString().slice(0, 10) : "—";
  const expires = lic.expires_at ? new Date(lic.expires_at).toISOString().slice(0, 10) : "—";
  const doPrint = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-auto p-4 sm:p-8 print:bg-white print:p-0" data-testid="print-modal">
      <div className="max-w-2xl mx-auto bg-white text-black rounded-2xl p-8 print:rounded-none print:p-6 print:shadow-none" id="print-target">
        {/* Header */}
        <div className="flex items-center justify-between pb-6 border-b-2 border-orange-500">
          <div className="flex items-center gap-3">
            <img src="/lhs-logo.png" alt="LHS Unlocker" className="w-14 h-14 object-contain" />
            <div>
              <div className="font-black text-2xl tracking-wider">LHS <span className="text-orange-500">UNLOCKER</span></div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Preuve d'activation officielle</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Référence</div>
            <div className="font-mono text-orange-500 font-bold">{lic.activation_ref || lic.order_number}</div>
          </div>
        </div>

        {/* Status badge */}
        <div className="my-6 flex justify-center">
          <div className="inline-block px-6 py-2 bg-emerald-500 text-white font-black uppercase tracking-widest rounded-full text-sm">
            ✓ Licence Activée
          </div>
        </div>

        {/* Body table */}
        <table className="w-full border-collapse text-sm">
          <tbody>
            <tr className="border-b">
              <td className="py-2 text-zinc-500 w-1/3">Technicien LHSunlocker</td>
              <td className="py-2 font-semibold">{user?.name} ({user?.email})</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Logiciel</td>
              <td className="py-2 font-bold">{lic.tool_name}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Compte du logiciel</td>
              <td className="py-2 font-mono">{lic.tp_username || "—"}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">E-mail du compte</td>
              <td className="py-2 font-mono text-xs">{lic.tp_email || "—"}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Durée</td>
              <td className="py-2 font-semibold">{PLAN_LABELS[lic.plan] || lic.plan.toUpperCase()}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Montant</td>
              <td className="py-2 font-semibold font-mono">{lic.price} {lic.currency}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Date d'activation</td>
              <td className="py-2 font-mono">{activated}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Date d'expiration</td>
              <td className="py-2 font-mono">{expires}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 text-zinc-500">Statut</td>
              <td className="py-2 font-black text-emerald-600">ACTIVÉ</td>
            </tr>
            <tr>
              <td className="py-2 text-zinc-500">Référence transaction</td>
              <td className="py-2 font-mono font-bold text-orange-500">{lic.activation_ref || "—"}</td>
            </tr>
          </tbody>
        </table>

        <div className="mt-8 pt-6 border-t text-center text-xs text-zinc-500 leading-relaxed">
          Cette preuve atteste de l'activation officielle de votre licence auprès de LHS Unlocker.<br/>
          Conservez-la précieusement. Pour toute question : <b>lhsunlocker@gmail.com</b>
        </div>

        <div className="mt-6 flex gap-3 justify-center print:hidden">
          <button onClick={onClose} className="px-6 py-2 border-2 border-zinc-300 rounded-lg">Fermer</button>
          <button onClick={doPrint} className="px-6 py-2 bg-orange-500 text-white font-bold rounded-lg inline-flex items-center gap-2">
            <Printer className="w-4 h-4" /> Imprimer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MesLicences3PPage() {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printLic, setPrintLic] = useState(null);

  useEffect(() => {
    axios.get(`${API}/store/orders/mine/activated`)
      .then((r) => setLicenses(r.data || []))
      .catch(() => setLicenses([]))
      .finally(() => setLoading(false));
  }, []);

  const active = licenses.filter((l) => !l.is_expired);
  const expired = licenses.filter((l) => l.is_expired);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="mes-licences-3p-page">
      {/* Header */}
      <div className="card-lhs p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
              <Award className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Mes achats validés</div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Mes licences tierces</h1>
              <p className="text-zinc-400 text-sm mt-1">
                Preuves officielles des licences 3rd party activées par l'administrateur LHSunlocker.
              </p>
            </div>
          </div>
          <Link to="/store" data-testid="go-to-store"
            className="btn-orange rounded-lg px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> Acheter un nouveau logiciel
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-20">Chargement…</div>
      ) : licenses.length === 0 ? (
        <div className="card-lhs p-10 text-center" data-testid="no-licenses">
          <Award className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="font-display font-bold text-lg mb-2">Aucune licence tierce activée</h3>
          <p className="text-zinc-400 text-sm max-w-md mx-auto mb-4">
            Vos achats de logiciels tiers validés par l'administrateur apparaîtront ici, avec leur preuve d'activation imprimable.
          </p>
          <Link to="/store" className="btn-orange inline-flex rounded-lg px-6 py-2 text-sm items-center gap-2">
            <ShoppingCart className="w-4 h-4" /> Parcourir le catalogue
          </Link>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <h2 className="font-display font-bold uppercase tracking-wider text-sm">Licences actives ({active.length})</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {active.map((lic) => <LicenseCard key={lic.id} lic={lic} onPrint={setPrintLic} />)}
              </div>
            </section>
          )}
          {expired.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-zinc-500" />
                <h2 className="font-display font-bold uppercase tracking-wider text-sm text-zinc-500">Licences expirées ({expired.length})</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {expired.map((lic) => <LicenseCard key={lic.id} lic={lic} onPrint={setPrintLic} />)}
              </div>
            </section>
          )}
        </>
      )}

      {printLic && <PrintableProof lic={printLic} user={user} onClose={() => setPrintLic(null)} />}
    </div>
  );
}
